// @ts-nocheck
// Generate + download a hard-copy PDF of a registration's Release Statement (and
// any required player waivers) so an admin can hand a blank form to a walk-up
// player to sign on paper. Built with jsPDF primitives (already installed) — a
// real PDF with NO browser chrome (no URL, page number, or date), crisp
// selectable text, and full control over per-document page breaks + signature
// blocks. Renders the react-quill HTML as styled text.
import { jsPDF } from 'jspdf';
import { saveDoc } from '@/utils/scoresheets/pdfCommon';
import { axios } from '@/store/axios';
import helpers from '@/utils/helpers';
import { toast } from 'sonner';

const baseUrl = `${process.env.API_URL}/api/v1`;

// Letter, points. jsPDF default text origin is the baseline.
const PAGE = { w: 612, h: 792, margin: 48 };
const CONTENT_W = PAGE.w - PAGE.margin * 2;
const PAGE_BOTTOM = PAGE.h - PAGE.margin;

const HEADING_LEVEL = { H1: 1, H2: 2, H3: 3, H4: 4, H5: 4, H6: 4 };

// ---- HTML → blocks (PURE, unit-testable) -------------------------------------
// Block = { type:'heading', level, runs } | { type:'para', runs }
//       | { type:'listitem', ordered, index, runs } | { type:'spacer' }
// Run   = { text, bold?, italic?, underline? }   ('\n' text = forced line break)
export function parseHtmlToBlocks(html) {
  const blocks = [];
  const raw = String(html ?? '');
  if (!raw.trim()) return blocks;

  let root;
  try {
    root = new DOMParser().parseFromString(raw, 'text/html').body;
  } catch (e) {
    return [{ type: 'para', runs: [{ text: raw }] }];
  }

  // Collapse whitespace (incl. &nbsp; →  , which \s matches) to single spaces.
  const clean = (t) => String(t || '').replace(/\s+/g, ' ');

  const collectRuns = (node, style, runs) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === 3) {
        const text = clean(child.textContent);
        if (text) runs.push({ text, ...style });
        return;
      }
      if (child.nodeType !== 1) return;
      const tag = child.tagName;
      if (tag === 'BR') { runs.push({ text: '\n' }); return; }
      const next = { ...style };
      if (tag === 'STRONG' || tag === 'B') next.bold = true;
      if (tag === 'EM' || tag === 'I') next.italic = true;
      if (tag === 'U' || tag === 'A' || tag === 'INS') next.underline = true;
      collectRuns(child, next, runs);
    });
  };
  const runsOf = (node) => {
    const runs = [];
    collectRuns(node, {}, runs);
    return runs;
  };
  const hasText = (runs) => runs.some((r) => r.text.trim());

  const walk = (node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === 3) {
        const text = clean(child.textContent);
        if (text.trim()) blocks.push({ type: 'para', runs: [{ text }] });
        return;
      }
      if (child.nodeType !== 1) return;
      const tag = child.tagName;
      if (HEADING_LEVEL[tag]) {
        const runs = runsOf(child);
        if (hasText(runs)) blocks.push({ type: 'heading', level: HEADING_LEVEL[tag], runs });
      } else if (tag === 'UL' || tag === 'OL') {
        const ordered = tag === 'OL';
        let idx = 0;
        child.childNodes.forEach((li) => {
          if (li.nodeType === 1 && li.tagName === 'LI') {
            idx += 1;
            blocks.push({ type: 'listitem', ordered, index: idx, runs: runsOf(li) });
          }
        });
      } else if (tag === 'DIV') {
        const blockKids = Array.from(child.children).some((c) =>
          /^(P|DIV|UL|OL|H[1-6]|BLOCKQUOTE)$/.test(c.tagName));
        if (blockKids) { walk(child); return; }
        const runs = runsOf(child);
        if (hasText(runs)) blocks.push({ type: 'para', runs });
        else blocks.push({ type: 'spacer' });
      } else if (tag === 'P' || tag === 'BLOCKQUOTE') {
        const runs = runsOf(child);
        if (hasText(runs)) blocks.push({ type: 'para', runs });
        else blocks.push({ type: 'spacer' });
      } else if (tag === 'BR') {
        blocks.push({ type: 'spacer' });
      } else if (tag === 'IMG' || tag === 'TABLE' || tag === 'HR' || tag === 'STYLE' || tag === 'SCRIPT') {
        // skip non-text content
      } else {
        const runs = runsOf(child);
        if (hasText(runs)) blocks.push({ type: 'para', runs });
      }
    });
  };

  walk(root);
  return blocks;
}

// ---- jsPDF rendering ----------------------------------------------------------
function setFont(doc, style, size) {
  const variant = style.bold && style.italic ? 'bolditalic'
    : style.bold ? 'bold' : style.italic ? 'italic' : 'normal';
  doc.setFont('helvetica', variant);
  doc.setFontSize(size);
}

// Lay out inline runs with word wrapping + mixed fonts. `top` is the top of the
// first line; returns the bottom (top + lineHeight) of the last line. Adds pages
// as needed.
function layoutRuns(doc, runs, opts) {
  const { x, width, size } = opts;
  const lineHeight = opts.lineHeight ?? size * 1.4;
  const ascent = size * 0.8;
  let top = opts.top;
  let cursorX = x;
  let atLineStart = true;

  const ensure = () => {
    if (top + lineHeight > PAGE_BOTTOM) { doc.addPage(); top = PAGE.margin; cursorX = x; atLineStart = true; }
  };
  const wrap = () => { top += lineHeight; cursorX = x; atLineStart = true; ensure(); };
  ensure();

  const tokens = [];
  runs.forEach((r) => {
    if (r.text === '\n') { tokens.push({ nl: true }); return; }
    r.text.split(/(\s+)/).forEach((p) => {
      if (p !== '') tokens.push({ text: p, bold: r.bold, italic: r.italic, underline: r.underline, space: /^\s+$/.test(p) });
    });
  });

  for (const tok of tokens) {
    if (tok.nl) { wrap(); continue; }
    setFont(doc, tok, size);
    const w = doc.getTextWidth(tok.text);
    if (tok.space) {
      if (atLineStart) continue;
      if (cursorX + w > x + width) { wrap(); continue; }
      cursorX += w;
      continue;
    }
    if (!atLineStart && cursorX + w > x + width) wrap();
    const baseline = top + ascent;
    doc.setTextColor(0);
    doc.text(tok.text, cursorX, baseline);
    if (tok.underline) {
      doc.setLineWidth(0.5);
      doc.line(cursorX, baseline + 1.5, cursorX + w, baseline + 1.5);
    }
    cursorX += w;
    atLineStart = false;
  }
  return top + lineHeight;
}

function renderBlocks(doc, blocks, startTop) {
  let y = startTop;
  const x = PAGE.margin;
  for (const b of blocks) {
    if (y + 24 > PAGE_BOTTOM) { doc.addPage(); y = PAGE.margin; }
    if (b.type === 'spacer') { y += 8; continue; }
    if (b.type === 'heading') {
      const size = b.level <= 1 ? 15 : b.level === 2 ? 13 : 12;
      y += 8;
      y = layoutRuns(doc, b.runs.map((r) => ({ ...r, bold: true })), { top: y, x, width: CONTENT_W, size, lineHeight: size * 1.3 });
      y += 4;
    } else if (b.type === 'listitem') {
      const size = 11;
      const indent = 20;
      setFont(doc, {}, size);
      doc.setTextColor(0);
      doc.text(b.ordered ? `${b.index}.` : '•', x + 4, y + size * 0.8);
      y = layoutRuns(doc, b.runs, { top: y, x: x + indent, width: CONTENT_W - indent, size });
      y += 3;
    } else {
      const size = 11;
      y = layoutRuns(doc, b.runs, { top: y, x, width: CONTENT_W, size });
      y += 6;
    }
  }
  return y;
}

function drawDocTitle(doc, title, top) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.setTextColor(0);
  doc.text(String(title || 'Release Statement'), PAGE.margin, top + 17 * 0.8);
  const lineY = top + 17 * 0.8 + 8;
  doc.setDrawColor(0);
  doc.setLineWidth(1);
  doc.line(PAGE.margin, lineY, PAGE.w - PAGE.margin, lineY);
  return lineY + 20;
}

function drawSignatureBlock(doc, top) {
  let y = top + 28;
  if (y + 96 > PAGE_BOTTOM) { doc.addPage(); y = PAGE.margin + 12; }
  const x = PAGE.margin;
  const labelW = 96;
  const row = (label, lineTo) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(70);
    doc.text(label, x, y);
    doc.setTextColor(0);
    doc.setDrawColor(0);
    doc.setLineWidth(0.75);
    doc.line(x + labelW, y + 2, lineTo, y + 2);
    y += 32;
  };
  row('Printed Name', PAGE.w - PAGE.margin);
  row('Signature', PAGE.w - PAGE.margin);
  row('Date', x + labelW + 190);
}

// PURE-ish: assemble the documents and render the whole PDF.
export function buildReleaseStatementPdf(registration, waiverDocs = []) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });

  const documents = [];
  const rs = (registration?.releaseStatement || '').trim();
  if (rs) documents.push({ title: 'Release Statement', html: rs });
  (waiverDocs || []).forEach((w) => {
    const html = (w?.html || '').trim();
    if (html) documents.push({ title: w?.title || w?.name || 'Waiver', html });
  });

  if (!documents.length) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.text('No release statement or waiver is configured for this registration.', PAGE.margin, PAGE.margin + 24);
    return doc;
  }

  documents.forEach((d, i) => {
    if (i > 0) doc.addPage();
    let y = PAGE.margin;
    y = drawDocTitle(doc, d.title, y);
    y = renderBlocks(doc, parseHtmlToBlocks(d.html), y);
    drawSignatureBlock(doc, y);
  });
  return doc;
}

function filenameFor(registration) {
  const name = (
    registration?.tournament?.name
    || registration?.contest?.name
    || registration?.seasonName
    || registration?.league?.displayName
    || registration?.league?.name
    || 'Registration'
  ).replace(/[^\w\- ]+/g, '').trim() || 'Registration';
  return `Release Form - ${name}.pdf`;
}

// True when there is something to put on the form (used to gate the button).
export function hasPrintableReleaseContent(registration) {
  return (
    !!(registration?.releaseStatement || '').trim()
    || (registration?.playerWaivers || []).some((w) => w?.required)
  );
}

/**
 * Fetch any required waiver docs, then generate + download the Release Statement
 * (+ those waivers) as a PDF. Tolerates per-waiver load failures; no-ops (with a
 * toast) when there is nothing to download.
 */
export async function downloadReleaseStatement(registration) {
  try {
    const requiredWaiverIds = (registration?.playerWaivers || [])
      .filter((w) => w?.required)
      .map((w) => helpers.getObjId(w?.waiver))
      .filter(Boolean);

    const waiverDocs = [];
    for (const id of requiredWaiverIds) {
      try {
        const { data } = await axios.get(`${baseUrl}/waivers/${id}`);
        const w = data?.result || data;
        if (w && w.html) waiverDocs.push(w);
      } catch (e) {
        console.error('downloadReleaseStatement: failed to load waiver', id, e);
        toast.error('A required waiver could not be loaded and was left off the form.');
      }
    }

    const hasContent =
      !!(registration?.releaseStatement || '').trim()
      || waiverDocs.some((w) => (w?.html || '').trim());
    if (!hasContent) {
      toast.error('Nothing to download: no release statement or waiver is configured.');
      return;
    }

    saveDoc(buildReleaseStatementPdf(registration, waiverDocs), filenameFor(registration));
  } catch (e) {
    console.error('downloadReleaseStatement:', e);
    toast.error('Could not generate the release form PDF.');
  }
}
